/**
 * 今月の残りを小さな円で見せる。押すと今月の内訳（日付・用途・使った割合）が開く。
 * プレミアム以外は、内訳の上にひとつ上のプランの目安とアップグレードの案内を出す。
 *
 * 内訳には本文を出さない。AI を使わない用途は 0% と出る。
 * 内訳は開いたときだけ読む（GET /api/chat/usage/breakdown）。
 * 会話ごとの記録で説明できない分（記録を始める前の利用・記録の失敗）は「そのほか」にまとめ、
 * 円の割合と内訳の合計を食い違わせない。
 */

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { chatQueryKeys } from '@/queryKeys/chat';
import { getChatUsageBreakdown, type ChatUsage } from '@/services/chat';
import { chatUpgradePlan, formatResetDate, remainingPercent } from '@/utils/chatUsage';
import { ChatUpgradeLink } from '@/components/chat/ChatUsageNotice';

function formatDay(isoDate: string): string {
  const [, month, day] = isoDate.split('-');
  return month && day ? `${Number(month)}/${Number(day)}` : isoDate;
}

// これより小さい差は丸めの誤差として「そのほか」を出さない
const UNTRACKED_MIN_RATIO = 0.005;

function percent(ratio: number): string {
  const value = ratio * 100;
  if (value > 0 && value < 1) return '1%未満';
  return `${Math.round(value)}%`;
}

function Ring({ remaining }: { remaining: number }) {
  const angle = Math.max(0, Math.min(100, remaining)) * 3.6;
  return (
    <span
      aria-hidden="true"
      className="relative inline-flex h-6 w-6 items-center justify-center rounded-full"
      style={{
        background: `conic-gradient(var(--color-primary-600) ${angle}deg, var(--color-text-200) 0deg)`,
      }}
    >
      <span className="h-4 w-4 rounded-full bg-white" />
    </span>
  );
}

function Breakdown({ onOpen }: { onOpen: (conversationId: string) => void }) {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const query = useQuery({
    queryKey: chatQueryKeys.breakdown(userId),
    queryFn: getChatUsageBreakdown,
    staleTime: 30_000,
  });
  if (query.isLoading) {
    return <p className="text-xs text-text-500">読み込んでいます…</p>;
  }
  if (query.isError || !query.data) {
    return <p className="text-xs text-text-500">内訳を読めませんでした。</p>;
  }
  const items = query.data.items ?? [];
  const tracked = items.reduce((sum, item) => sum + item.usedRatio, 0);
  const untracked = query.data.usage.usedRatio - tracked;
  const showUntracked = untracked >= UNTRACKED_MIN_RATIO;
  if (items.length === 0 && !showUntracked) {
    return <p className="text-xs text-text-500">今月はまだ使っていません。</p>;
  }
  return (
    <ul className="max-h-60 space-y-1 overflow-y-auto text-xs">
      {items.map((item, index) => (
        <li key={`${item.date}-${item.conversationId}-${index}`}>
          {/* 押すとその会話を開く（30 日を過ぎた・消した会話は開いた先で知らせる） */}
          <button
            type="button"
            onClick={() => onOpen(item.conversationId)}
            className="flex w-full gap-2 rounded px-1 py-0.5 text-left tabular-nums hover:bg-primary-50"
          >
            <span className="w-10 flex-none text-text-500">{formatDay(item.date)}</span>
            <span className="flex-1 truncate text-text-700 underline decoration-text-300 underline-offset-2">
              {item.label}
            </span>
            <span className="flex-none text-text-500">{percent(item.usedRatio)}</span>
          </button>
        </li>
      ))}
      {showUntracked && (
        <li className="flex gap-2 px-1 py-0.5 tabular-nums">
          <span className="w-10 flex-none" />
          <span className="flex-1 truncate text-text-700">そのほか</span>
          <span className="flex-none text-text-500">{percent(untracked)}</span>
        </li>
      )}
    </ul>
  );
}

export default function ChatUsageRing({
  usage,
  onNavigate,
  onOpenConversation,
}: {
  usage: ChatUsage | undefined;
  /** 料金ページへ移るとき（パネルを閉じる） */
  onNavigate: () => void;
  /** 内訳の行を押したとき（その会話を開く） */
  onOpenConversation: (conversationId: string) => void;
}) {
  if (!usage) return null;
  const remaining = remainingPercent(usage);
  const upgrade = chatUpgradePlan(usage);
  return (
    <Popover className="relative">
      <PopoverButton
        className="flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-xs text-text-600 hover:bg-text-100"
        aria-label={`今月の残り ${remaining}%。押すと内訳を表示`}
      >
        <Ring remaining={remaining} />
        <span className="tabular-nums">残り{remaining}%</span>
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom end"
        className="z-[60] mt-2 w-64 rounded-xl bg-white p-3 shadow-lg ring-1 ring-text-900/10 transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
      >
        {({ close }) => (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-text-900">今月の残り {remaining}%</p>
            <div className="h-2 overflow-hidden rounded-full bg-text-100">
              <div className="h-full bg-primary-600" style={{ width: `${remaining}%` }} />
            </div>
            <p className="text-[11px] text-text-500">
              {formatResetDate(usage.resetsAt)}に回復します。AIを使わない用途では減りません。
            </p>
            {upgrade && (
              <ChatUpgradeLink
                plan={upgrade}
                onNavigate={onNavigate}
                className="block text-[11px] text-primary-700"
              />
            )}
            <Breakdown
              onOpen={(id) => {
                close();
                onOpenConversation(id);
              }}
            />
            <p className="text-[10px] text-text-400">行を押すと、その会話を開けます。</p>
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
}
